import { useCallback, useEffect, useState } from "react";
import { parse, stringify } from "query-string";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useLocation } from "@/hooks/useLocation";
import { navigate } from "@/utilities/navigate";
import {
  consumeInteracReturnUrl,
  hasInteracReturnUrl,
  saveInteracReturnUrl,
} from "@/features/wallet/utils/interac";

/**
 * Records where the modal was open before Interac takes the user off site.
 */
export const useStashInteracReturnUrl = () => {
  const userId = useUserId();
  const { pathnameWithoutLocale: pathname, search } = useLocation();

  return useCallback(() => {
    if (!userId) {
      return;
    }

    // the modal is url-driven on the way back in, same as a Skrill return
    const params = stringify({
      ...parse(search),
      method: "interac",
      depositModal: "true",
    });

    saveInteracReturnUrl(userId, `${pathname}?${params}`);
  }, [userId, pathname, search]);
};

/**
 * Replays the stashed url on the way back from Interac, so the user lands on
 * the page they left with the deposit modal reopened rather than on whichever
 * page Gigadat sent them to.
 */
export const useInteracDepositReturn = () => {
  const userId = useUserId();
  const isAuthLoading = useAuthStore((state) => state.status === "loading");
  const { pathnameWithoutLocale: pathname } = useLocation();

  // seeded synchronously so the first render already knows to hold the modal
  // shut, before auth has resolved far enough to read the account's key
  const [isReturning, setIsReturning] = useState(hasInteracReturnUrl);

  useEffect(() => {
    if (!isReturning || isAuthLoading) {
      return;
    }

    const destination = userId ? consumeInteracReturnUrl(userId) : null;
    const [destinationPath, destinationSearch] = destination?.split("?") ?? [];

    // an expired or foreign token leaves nothing to replay, and landing on the
    // stashed page means the bounce is done
    if (!destinationPath || destinationPath === pathname) {
      setIsReturning(false);
      return;
    }

    const search = stringify({
      ...parse(destinationSearch ?? ""),
    });

    navigate(search ? `${destinationPath}?${search}` : destinationPath);
  }, [isReturning, isAuthLoading, userId, pathname]);

  return isReturning;
};
